import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import firebaseInit from "../../firebase";
import { logActivity } from "../firebaseActions/activityLog";
import { UploadToStorage } from "../firebaseActions/uploaderActions";
import { RemoveFileExt } from "../../utilities/utilityService";
import { deleteObject, listAll, ref } from "firebase/storage";
import { sendEmail } from "../mail/emailService";
import {
  DEFAULT_APPROVERS,
  DEFAULT_APPROVERS_SYD,
  getApproversForRequester,
} from "../../helpers/merch-manager/helper";

const fireStoreStorage = firebaseInit.firestore;
const colRef = collection(fireStoreStorage, "merchendise_manager");
const colRef_req = collection(fireStoreStorage, "merch_requests");
const storage = firebaseInit.storage;

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildAsset = ({ url, type, fileName, email }) => ({
  id: crypto.randomUUID(),
  url,
  type: type ?? "image",
  fileName,
  addedBy: email,
});

const uploadMerchAssets = async ({ assets, itemId, email }) => {
  const results = await Promise.all(
    assets.map(async ({ file, fileName }) => {
      const url = await UploadToStorage(
        fileName,
        file,
        "merch_manager",
        itemId,
        RemoveFileExt(fileName),
        () => {},
        () => {},
        email,
      );
      return url ? buildAsset({ url, fileName, email }) : null;
    }),
  );
  return results.filter(Boolean);
};

// ---------------------------------------------------------------------------
// getMerch
// ---------------------------------------------------------------------------

export const getMerch = async ({ setIsLoadingMerch }) => {
  setIsLoadingMerch(true);
  try {
    const q = query(colRef, orderBy("updatedAt", "desc"));

    const snapshot = await getDocs(q);
    if (snapshot.empty) return [];

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (err) {
    console.error("getMerch failed:", err);
    return [];
  } finally {
    setIsLoadingMerch(false);
  }
};

// ---------------------------------------------------------------------------
// newMerch
// ---------------------------------------------------------------------------

export const createMerchItem = async ({
  form,
  email = null,
  setIsLoading,
  setResponse,
  formRef,
  onSuccess,
  isVerified,
}) => {
  setIsLoading(true);
  try {
    if (!isVerified) {
      throw new Error("You are not authorized to create merch items.");
    }
    const snap = await getDocs(colRef);
    const exists = snap.docs.some(
      (doc) => doc.data().item_name === form.item_name,
    );
    if (exists) {
      throw new Error(
        "An item with this name already exists. Please choose a different name.",
      );
    }

    if (form.item_count < 1) {
      throw new Error("At least one item is required.");
    }

    const docRef = doc(colRef);

    await setDoc(docRef, {
      id: docRef.id,
      item_name: form.item_name,
      item_count: form.item_count,
      assets: [],
      item_purpose: form.item_purpose ?? "",
      item_location: form.item_location ?? "",
      storage_location: form.storage_location ?? "melb",
      created_by: email ?? form.created_by,
      updated_by: email ?? form.updated_by,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      restock_expected: form.restock_expected ?? false,
    });

    if (Array.isArray(form.assets) && form.assets.length > 0) {
      const assets = await uploadMerchAssets({
        assets: form.assets,
        itemId: docRef.id,
        email,
      });
      await updateDoc(docRef, {
        assets,
      });
    }

    setResponse("add_new_merch", true);
    if (formRef?.current) formRef.current.reset();
    if (onSuccess) {
      onSuccess();
      //log
      logActivity({
        category: "merch_manager",
        event: "merch_create_success",
        email,
        message: "Created merch item: " + form.item_name,
      });
    }
  } catch (err) {
    console.error("createMerchItem failed:", err);
    setResponse("add_new_merch", false, err.message);

    //log
    logActivity({
      category: "merch_manager",
      event: "merch_create_failure",
      email,
      message:
        "Failed to create merch item: " + form.item_name + " — " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// editMerch
// ---------------------------------------------------------------------------

export const editMerchItem = async ({
  isVerified,
  form,
  email,
  setResponse,
  onSuccess,
  setIsLoading,
  formRef,
}) => {
  setIsLoading(true);
  try {
    if (!isVerified) {
      throw new Error("You are not authorized to edit merch items.");
    }
    const itemRef = doc(colRef, form.id);

    const existingAssets = form.assets.filter((a) => a.url && !a.file);
    const newFiles = form.assets.filter((a) => a.file);

    const uploadedAssets =
      newFiles.length > 0
        ? await uploadMerchAssets({ assets: newFiles, itemId: form.id, email })
        : [];

    await updateDoc(itemRef, {
      item_name: form.item_name,
      item_count: form.item_count,
      item_purpose: form.item_purpose,
      item_location: form.item_location,
      assets: [...existingAssets, ...uploadedAssets],
      updated_by: email,
      updatedAt: serverTimestamp(),
      restock_expected: form.restock_expected ?? false,
      storage_location: form.storage_location ?? "melb",
    });

    setResponse("update_merch", true);
    if (formRef?.current) formRef.current.reset();
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_manager",
      event: "merch_update_success",
      email,
      message: "Updated merch item: " + form.id,
    });
  } catch (err) {
    console.error("updateMerchItem failed:", err);
    setResponse("update_merch", false, err.message);
    logActivity({
      category: "merch_manager",
      event: "merch_update_failure",
      email,
      message: "Failed to update merch item: " + form.id + " — " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// submitMerchRequest
// ---------------------------------------------------------------------------

export const submitMerchRequest = async ({
  form,
  email = null,
  requesterTeam = "sales",
  requesterName,
  setIsLoading,
  setResponse,
  formRef,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    const itemRef = doc(colRef, form.id);
    const itemSnap = await getDoc(itemRef);
    const itemData = itemSnap.data();
    const currentCount = Number(itemData?.item_count ?? 0);
    const requestedCount = Number(form.item_count);

    if (requestedCount < 1) {
      throw new Error("At least one item is required.");
    }

    // guard — recheck stock at submit time in case it changed since page load
    if (requestedCount > currentCount) {
      throw new Error(
        `Only ${currentCount} item${currentCount === 1 ? "" : "s"} available. Please reduce your count.`,
      );
    }

    const docRef = doc(colRef_req);

    await setDoc(docRef, {
      req_id: docRef.id,
      item_id: form.id,
      item_name: form.item_name,
      item_count: requestedCount,
      purpose: form.item_purpose ?? "",
      status: "pending",
      requested_by: email,
      requester_name: requesterName,
      requester_team: requesterTeam,
      approved_by: null,
      approved_by_email: null,
      approved_at: null,
      declined_by: null,
      declined_by_email: null,
      declined_at: null,
      decline_reason: null,
      picked_up_by: null,
      picked_up_at: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // deduct stock on submit
    await updateDoc(itemRef, {
      item_count: currentCount - requestedCount,
      // updated_by: email,
      updatedAt: serverTimestamp(),
    });

    const storageLocation = itemData?.storage_location?.toLowerCase() ?? "";

    const notifyEmails =
      storageLocation === "syd"
        ? DEFAULT_APPROVERS_SYD
        : storageLocation === "melb"
          ? DEFAULT_APPROVERS
          : DEFAULT_APPROVERS;

    sendEmail({
      to: [...new Set([...getApproversForRequester(email), ...notifyEmails])],
      subject: `New merch request — ${form.item_name}`,
      // templateId: MERCH_TEMPLATE_ID,
      templateData: {
        requestUrl: `${window.location.origin}/merchandise-manager/${docRef.id}?tab=pending`,
        title: "New Merch Request",
        requesterName,
        itemName: form.item_name,
        itemCount: requestedCount,
        purpose: form.item_purpose ?? "—",
        status: "Pending Review",
        note: null,
        declineReason: null,
      },
      sentBy: email,
      email_for: "merchendise",
    });

    setResponse("submit_merch_request", true);
    if (formRef?.current) formRef.current.reset();
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_request",
      event: "merch_request_submit_success",
      email,
      message: `${email} requested ${requestedCount}x ${form.item_name}`,
    });
  } catch (err) {
    console.error("submitMerchRequest failed:", err);
    setResponse("submit_merch_request", false, err.message);
    logActivity({
      category: "merch_request",
      event: "merch_request_submit_failure",
      email,
      message: "Failed to submit request: " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// deleteMerchItem
// ---------------------------------------------------------------------------

export const deleteMerchItem = async ({
  isVerified,
  itemId,
  email,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    if (!isVerified) {
      throw new Error("You are not authorized to delete merch items.");
    }
    const activeRequestsSnap = await getDocs(
      query(
        colRef_req,
        where("item_id", "==", itemId),
        where("status", "in", ["pending", "accepted", "declined"]),
      ),
    );

    if (!activeRequestsSnap.empty) {
      throw new Error(
        `Cannot delete — this item has ${activeRequestsSnap.size} request${activeRequestsSnap.size === 1 ? "" : "s"} associated with it (active or processed).`,
      );
    }

    // 1. Delete all files in the folder
    const folderRef = ref(storage, `merch_manager/${itemId}`);
    const { items } = await listAll(folderRef);
    await Promise.all(items.map((fileRef) => deleteObject(fileRef)));

    // 2. Delete the Firestore document
    await deleteDoc(doc(colRef, itemId));

    setResponse?.("delete_merch", true);
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_request",
      event: "merch_item_delete_success",
      email: email,
      message: `Deleted merch item: ${itemId}`,
    });
  } catch (err) {
    console.error("deleteMerchItem failed:", err);
    setResponse?.("delete_merch", false, err.message);
    logActivity({
      category: "merch_request",
      event: "merch_item_delete_failed",
      email: email,
      message: `Failed to deleted merch item: ${itemId}`,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// getPending
// ---------------------------------------------------------------------------

export const getPendingRequests = async ({ setIsLoading }) => {
  setIsLoading(true);
  try {
    const q = query(
      colRef_req,
      where("status", "==", "pending"),
      orderBy("createdAt", "desc"),
    );
    const snap = await getDocs(q);
    if (snap.empty) return [];

    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("getPendingRequests failed:", err);
    return [];
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// getAccepted
// ---------------------------------------------------------------------------

export const getAcceptedRequests = async ({ setIsLoading }) => {
  setIsLoading(true);
  try {
    const q = query(
      colRef_req,
      where("status", "in", ["accepted", "picked_up"]),
      orderBy("createdAt", "desc"),
    );
    const snap = await getDocs(q);
    if (snap.empty) return [];

    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("getAcceptedRequests failed:", err);
    return [];
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// getDeclined
// ---------------------------------------------------------------------------

export const getDeclinedRequests = async ({ setIsLoading }) => {
  setIsLoading(true);
  try {
    const q = query(
      colRef_req,
      where("status", "==", "declined"),
      orderBy("declined_at", "desc"),
    );
    const snap = await getDocs(q);
    if (snap.empty) return [];

    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("getDeclinedRequests failed:", err);
    return [];
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// approveRequest
// ---------------------------------------------------------------------------

export const approveRequest = async ({
  itemId,
  requestId,
  note,
  approverEmail,
  approverName,
  requesterName,
  requesterEmail,
  itemName,
  itemCount,
  purpose,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    await updateDoc(doc(colRef_req, requestId), {
      status: "accepted",
      approved_by: approverName,
      approved_by_email: approverEmail,
      approved_at: serverTimestamp(),
      note: note ?? "",
      updatedAt: serverTimestamp(),
    });

    // fetch item to check location
    const itemSnap = await getDoc(doc(colRef, itemId));
    const storageLocation =
      itemSnap.data()?.storage_location?.toLowerCase() ?? "";

    const notifyEmails =
      storageLocation === "syd"
        ? DEFAULT_APPROVERS_SYD
        : storageLocation === "melb"
          ? DEFAULT_APPROVERS
          : DEFAULT_APPROVERS;

    sendEmail({
      to: notifyEmails,
      subject: `Approve merch request — ${itemName}`,
      // templateId: MERCH_TEMPLATE_ID,
      templateData: {
        requestUrl: `${window.location.origin}/merchandise-manager/${requestId}?tab=accepted`,
        title: "Approve Merch Request",
        requesterName,
        itemName: itemName,
        itemCount: itemCount,
        purpose: purpose ?? "—",
        status: "Approved",
        note: note,
        declineReason: null,
      },
      sentBy: approverEmail,
      email_for: "merchendise",
    });

    setResponse("approve_request", true);
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_request",
      event: "merch_request_approved",
      email: approverEmail,
      message: `${approverName} approved request: ${requestId}`,
    });
  } catch (err) {
    console.error("approveRequest failed:", err);
    setResponse("approve_request", false, err.message);
    logActivity({
      category: "merch_request",
      event: "merch_request_approve_failure",
      email: approverEmail,
      message: "Failed to approve: " + requestId + " — " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// declineRequest
// ---------------------------------------------------------------------------

export const declineRequest = async ({
  requestId,
  itemId,
  requestedCount,
  reason,
  note,
  declinerEmail,
  declinerName,
  requesterEmail,
  requesterName,
  itemName,
  itemCount,
  purpose,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    if (!reason?.trim())
      throw new Error("A reason is required when declining.");

    // restore stock since it was deducted on submit
    const itemRef = doc(colRef, itemId);
    const itemSnap = await getDoc(itemRef);
    const currentCount = Number(itemSnap.data()?.item_count ?? 0);

    await updateDoc(itemRef, {
      item_count: currentCount + requestedCount,
      updatedAt: serverTimestamp(),
    });

    await updateDoc(doc(colRef_req, requestId), {
      status: "declined",
      declined_by: declinerName,
      declined_by_email: declinerEmail,
      declined_at: serverTimestamp(),
      decline_reason: reason.trim(),
      note: note ?? "",
      updatedAt: serverTimestamp(),
    });

    sendEmail({
      to: requesterEmail,
      subject: `Decline merch request — ${itemName}`,
      // templateId: MERCH_TEMPLATE_ID,
      templateData: {
        requestUrl: `${window.location.origin}/merchandise-manager/${requestId}?tab=declined`,
        title: "Decline Merch Request",
        requesterName,
        itemName: itemName,
        itemCount: itemCount,
        purpose: purpose ?? "—",
        status: "Declined",
        note: note,
        declineReason: reason,
      },
      sentBy: declinerEmail,
      email_for: "merchendise",
    });

    setResponse("decline_request", true);
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_request",
      event: "merch_request_declined",
      email: declinerEmail,
      message: `${declinerName} declined request: ${requestId}`,
    });
  } catch (err) {
    console.error("declineRequest failed:", err);
    setResponse("decline_request", false, err.message);
    logActivity({
      category: "merch_request",
      event: "merch_request_decline_failure",
      email: declinerEmail,
      message: "Failed to decline: " + requestId + " — " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// getCounts
// ---------------------------------------------------------------------------

export const listenRequestCounts = (callback) => {
  return onSnapshot(colRef_req, (snapshot) => {
    let pending = 0;
    // let accepted = 0;

    snapshot.forEach((doc) => {
      const data = doc.data();

      if (data.status === "pending") pending++;
      /*   if (data.status === "accepted" || data.status === "picked_up") {
        accepted++;
      } */
    });

    callback({ pending /* accepted */ });
  });
};

// ---------------------------------------------------------------------------
// pickedUp
// ---------------------------------------------------------------------------

export const markPickedUp = async ({
  requestId,
  pickedUpBy,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    await updateDoc(doc(colRef_req, requestId), {
      status: "picked_up",
      picked_up_by: pickedUpBy,
      picked_up_at: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setResponse("mark_picked_up", true);
    if (onSuccess) onSuccess();

    logActivity({
      category: "merch_request",
      event: "merch_picked_up",
      email: pickedUpBy,
      message: `${pickedUpBy} marked request ${requestId} as picked up`,
    });
  } catch (err) {
    console.error("markPickedUp failed:", err);
    setResponse("mark_picked_up", false, err.message);
    logActivity({
      category: "merch_request",
      event: "merch_picked_up_failure",
      email: pickedUpBy,
      message: "Failed to mark picked up: " + requestId + " — " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};
